import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupLayout from "../SetupLayout/SetupLayout";
import { useSetupState, useSetupDispatch, resetSetup, clearPersistedSetupState } from "../../../auth/SetupContext";
import { API_BASE_URL, CLIENT_ID, REDIRECT_URI } from "../../../auth/config";
import { redirectToAuthorize } from "../../../utils/pkce";
import { POST_SETUP_FLAG } from "../../../auth/postSetupFlag";
import useBfcacheReload from "../../../utils/useBfcacheReload";
import formStyles from "../setupForm.module.scss";

export default function GrantUserAccess() {
  useBfcacheReload();
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();
  const { userId, clientId, redirectUri, username } = useSetupState();

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!userId) navigate("/setup/user", { replace: true });
  }, [userId, navigate]);

  const handleBack = () => {
    navigate("/", { replace: true });
    setTimeout(() => dispatch(resetSetup()), 0);
  };

  const handleFinish = async () => {
    setError("");
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/user-access`, { userId, clientId });
      // Clear persisted state directly (not via dispatch) so this still-
      // mounted page doesn't re-render with empty context fields and trip
      // its own guard - and the guards of the pages it would cascade
      // through - before the redirect below actually leaves the SPA.
      clearPersistedSetupState();
      // Setup is done - flag that any /callback error from here on must not
      // route back into the setup wizard (the wizard just finished; bouncing
      // back to e.g. /setup/client because of a slow-to-propagate backend
      // record is confusing and disallowed). Callback.jsx checks this flag.
      sessionStorage.setItem(POST_SETUP_FLAG, "1");
      // Rewrite the current history entry's URL to "/" (without a React
      // Router navigation - we're about to leave the SPA entirely) so that
      // whichever setup step we're leaving from, the entry sitting directly
      // behind the upcoming SSO redirect is always our login screen. Without
      // this, clicking the browser Back button while on the SSO page lands
      // back on whatever setup URL happened to be active (e.g.
      // /setup/client) instead of the login screen.
      window.history.replaceState(null, "", "/");
      // Go straight to the SSO authorize redirect instead of making the user
      // land on the login page and click "Login with Auth" again.
      await redirectToAuthorize({
        apiBaseUrl: API_BASE_URL,
        clientId: clientId || CLIENT_ID,
        redirectUri: redirectUri || REDIRECT_URI,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to grant user access");
      setIsSubmitting(false);
    }
  };

  return (
    <SetupLayout
      step={6}
      totalSteps={6}
      title="Grant user access"
      subtitle="Give this user access to the OAuth client to finish setup."
      error={error}
      onBack={handleBack}
      backDisabled={isSubmitting}
      loading={isSubmitting}
      loadingText="Finishing setup, redirecting to login..."
    >
      <div className={formStyles.fieldGroup}>
        <label className={formStyles.label}>User</label>
        <div className={formStyles.readOnlyValue}>{username || userId}</div>
      </div>
      <div className={formStyles.btnRow}>
        <button type="button" className={formStyles.primaryBtn} onClick={handleFinish} disabled={isSubmitting}>
          {isSubmitting ? "Finishing..." : "Finish setup & go to login"}
        </button>
      </div>
    </SetupLayout>
  );
}
